import { materialChoiceSchema, systemKindSchema } from "../services/busbarSelectionContract";
import type { SldElements } from "../services/sldElements";
import {
  FAULT_LEVEL_SOURCE_LABELS,
  FAULT_LEVEL_SOURCES,
  MATERIAL_CHOICE_LABELS,
  SYSTEM_KIND_LABELS,
  type BusbarSelectionDraft,
  type FaultLevelSource,
} from "./busbarSelectionDraft";
import { blockMemo, fieldKit, type BlockProps } from "./BusbarSelectionFields";
import { FaultRunSelector } from "./FaultRunSelector";
import { SldElementPicker } from "./SldElementPicker";
import { TYPED_HINT } from "./StarterCoordinationForm";

export { TYPED_HINT };

// The fieldsets of the study that are always shown: the definition, the fault level, the
// busbar and the closing notes. Like the optional blocks, each is a memoized component of its
// own slice of the draft.

// Written from the engine (backend/app/domain/electrical/busbar_selection/engine.py): with no
// peak current ip the peak withstand check (lines 541-546) and, once the supports are entered,
// the supports check (lines 489-494) are both Engineering review required.
export const NO_PEAK_NOTE =
  "This fault run has no peak current ip; the peak withstand check, and the supports check " +
  "once supports are entered, will be Engineering review required.";

export const STUDY_NOTES_HINT = "Part of the calculation record. Changing it creates a new revision.";

export const RUN_NOTE_HINT = "Not part of the calculation. Does not create a new revision.";

// Km is a multiplier, never a percentage (follow-up 23); the rule itself is the backend's.
export const MARGIN_FACTOR_HINT = "Multiplier, e.g. 1.25 for a 25 % margin.";

const DEFINITION_KEYS = ["code", "name", "busCode", "sldElementCode"] as const;

type DefinitionValues = Pick<BusbarSelectionDraft, (typeof DEFINITION_KEYS)[number]>;

type DefinitionProps = BlockProps<DefinitionValues> & {
  // The revision's single-line diagram, when it has one: the bus is picked from it.
  sldElements?: SldElements | null | undefined;
  onChooseBus: (busCode: string) => void;
};

export const StudyDefinition = blockMemo(function StudyDefinition({
  draft,
  onChange,
  disabled,
  sldElements,
  onChooseBus,
}: DefinitionProps) {
  const kit = fieldKit(draft, onChange);
  return (
    <fieldset disabled={disabled}>
      <legend>Study definition</legend>
      {kit.text("Study code", "code")}
      {kit.text("Study name", "name")}
      <SldElementPicker
        label="Bus code"
        name="busCode"
        value={draft.busCode}
        onChange={onChooseBus}
        categories={["bus"]}
        elements={sldElements}
      />
      <SldElementPicker
        label="Single-line diagram bus"
        name="sldElementCode"
        value={draft.sldElementCode}
        onChange={(sldElementCode) => onChange({ sldElementCode })}
        categories={["bus"]}
        elements={sldElements}
      />
    </fieldset>
  );
}, DEFINITION_KEYS);

const FAULT_LEVEL_KEYS = [
  "faultLevelSource",
  "faultRunId",
  "typedProspectiveCurrentKa",
  "typedPeakCurrentKa",
  "typedSystemNominalVoltageV",
  "typedFaultBusCode",
  "requiredFaultDurationS",
  "busCode",
] as const;

type FaultLevelValues = Pick<BusbarSelectionDraft, (typeof FAULT_LEVEL_KEYS)[number]>;

type FaultLevelProps = BlockProps<FaultLevelValues> & {
  revisionId: string | null;
  // Stable: the selector's effects depend on it.
  onFaultRunChange: (faultRunId: string) => void;
};

// The fault level comes from one of two places: a stored EOS-04 fault run of the active
// revision (the default) or a typed value, which the engine reports as Engineering review
// required. The payload carries only the chosen one (see busbarSelectionDraft).
export const FaultLevel = blockMemo(function FaultLevel({
  draft,
  onChange,
  disabled,
  revisionId,
  onFaultRunChange,
}: FaultLevelProps) {
  const kit = fieldKit(draft, onChange);
  return (
    <fieldset disabled={disabled}>
      <legend>Fault level</legend>
      <label>
        Fault level source
        <select
          name="faultLevelSource"
          value={draft.faultLevelSource}
          onChange={(event) =>
            onChange({ faultLevelSource: event.target.value as FaultLevelSource })
          }
        >
          {FAULT_LEVEL_SOURCES.map((source) => (
            <option key={source} value={source}>
              {FAULT_LEVEL_SOURCE_LABELS[source]}
            </option>
          ))}
        </select>
      </label>

      {draft.faultLevelSource === "LINKED" ? (
        <FaultRunSelector
          revisionId={revisionId}
          faultRunId={draft.faultRunId}
          onChange={onFaultRunChange}
          busCode={draft.busCode.trim()}
          withPeak
          noPeakNote={NO_PEAK_NOTE}
        />
      ) : (
        <>
          {kit.decimal("Prospective current Ik'' (kA)", "typedProspectiveCurrentKa")}
          {kit.decimal("Peak current ip (kA)", "typedPeakCurrentKa")}
          {kit.decimal("System nominal voltage (V)", "typedSystemNominalVoltageV")}
          {kit.text("Fault bus code", "typedFaultBusCode")}
          <p data-typed-hint="true">{TYPED_HINT}</p>
        </>
      )}
      {kit.decimal("Required fault duration (s)", "requiredFaultDurationS")}
    </fieldset>
  );
}, FAULT_LEVEL_KEYS);

const BUSBAR_KEYS = [
  "systemKind",
  "material",
  "maxBarsPerPhase",
  "designCurrentA",
  "loadCurrentA",
  "marginFactor",
  "siteAmbientC",
] as const;

type BusbarValues = Pick<BusbarSelectionDraft, (typeof BUSBAR_KEYS)[number]>;

export const BusbarBasics = blockMemo(function BusbarBasics({
  draft,
  onChange,
  disabled,
}: BlockProps<BusbarValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <fieldset disabled={disabled}>
      <legend>Busbar</legend>
      {kit.choice("System kind", "systemKind", systemKindSchema.options, SYSTEM_KIND_LABELS, false)}
      {kit.choice(
        "Conductor material",
        "material",
        materialChoiceSchema.options,
        MATERIAL_CHOICE_LABELS,
      )}
      {kit.decimal("Maximum bars per phase", "maxBarsPerPhase")}
      {kit.decimal("Design current (A)", "designCurrentA")}
      {kit.decimal("Load current (A)", "loadCurrentA")}
      {kit.text("Margin factor", "marginFactor", { decimal: true, hint: MARGIN_FACTOR_HINT })}
      {kit.decimal("Site ambient (°C)", "siteAmbientC")}
    </fieldset>
  );
}, BUSBAR_KEYS);

const CLOSING_KEYS = ["jurisdictionProfile", "notes", "runNotes"] as const;

type ClosingValues = Pick<BusbarSelectionDraft, (typeof CLOSING_KEYS)[number]>;

export const ClosingFields = blockMemo(function ClosingFields({
  draft,
  onChange,
  disabled,
}: BlockProps<ClosingValues>) {
  const kit = fieldKit(draft, onChange);
  return (
    <fieldset disabled={disabled}>
      <legend>Jurisdiction and notes</legend>
      <label>
        Jurisdiction profile
        <select
          name="jurisdictionProfile"
          value={draft.jurisdictionProfile}
          onChange={(event) => onChange({ jurisdictionProfile: event.target.value })}
        >
          <option value="IN">India (CEA Regulations, IS, CPWD)</option>
          <option value="IEC">IEC (international, no national layer)</option>
          <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
          <option value="EU">European Union (HD 60364) - reference data pending</option>
          <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
          <option value="AU_NZ">
            Australia / New Zealand (AS/NZS 3000) - reference data pending
          </option>
        </select>
      </label>
      {kit.text("Study notes", "notes", { hint: STUDY_NOTES_HINT })}
      {kit.text("Run note", "runNotes", { hint: RUN_NOTE_HINT })}
    </fieldset>
  );
}, CLOSING_KEYS);
